import React, { useState, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';

interface CompassProps {
  onRotationUpdate: (rotation: number) => void;
}

const CompassTracker: React.FC<CompassProps> = ({ onRotationUpdate }) => {
  const { camera } = useThree();
  const previousRotation = useRef<number>(0);

  useFrame(() => {
    // Get the camera's horizontal rotation (yaw) from its quaternion
    // We want to know which direction is "forward" for the camera on the XZ plane
    const direction = new THREE.Vector3();
    camera.getWorldDirection(direction);
    
    // Calculate the angle on the XZ plane
    // atan2 gives us the angle from the positive Z axis, counterclockwise
    // We negate direction.x to get the correct rotation direction
    const angle = Math.atan2(-direction.x, -direction.z);
    
    // Convert to degrees
    let degrees = (angle * 180 / Math.PI);
    
    // Normalize to 0-360 range
    if (degrees < 0) degrees += 360;
    
    // Handle smooth transition across 0/360 boundary
    const prev = previousRotation.current;
    const delta = degrees - prev;
    
    // If we jumped more than 180 degrees, we crossed the boundary
    if (delta > 180) {
      // We went from 359 to 0, continue rotating in the negative direction
      previousRotation.current = degrees - 360;
    } else if (delta < -180) {
      // We went from 0 to 359, continue rotating in the positive direction
      previousRotation.current = degrees + 360;
    } else {
      previousRotation.current = degrees;
    }
    
    onRotationUpdate(previousRotation.current);
  });

  return null;
};

interface CompassDisplayProps {
  rotation: number;
}

const CompassDisplay: React.FC<CompassDisplayProps> = ({ rotation }) => {
  // Normalize rotation for display (keep between 0-360)
  const normalizedRotation = ((rotation % 360) + 360) % 360;
  
  return (
    <div className="absolute bottom-4 right-4 w-24 h-24 bg-white/40 backdrop-blur-sm rounded-full shadow-lg border border-white/60 flex items-center justify-center">
      {/* Rotating compass rose */}
      <div 
        className="relative w-20 h-20"
        style={{ 
          transform: `rotate(${-rotation}deg)`,
          transition: 'transform 0.1s linear'
        }}
      >
        {/* North indicator (red) */}
        <div className="absolute inset-0 flex items-start justify-center">
          <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[16px] border-t-red-500" />
        </div>
        
        {/* South indicator */}
        <div className="absolute inset-0 flex items-end justify-center">
          <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-b-[16px] border-b-gray-400" />
        </div>
        
        {/* East indicator */}
        <div className="absolute inset-0 flex items-center justify-end">
          <div className="w-0 h-0 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent border-r-[12px] border-r-gray-500" />
        </div>
        
        {/* West indicator */}
        <div className="absolute inset-0 flex items-center justify-start">
          <div className="w-0 h-0 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent border-l-[12px] border-l-gray-500" />
        </div>
        
        {/* Cardinal direction labels */}
        <div className="absolute inset-0 flex items-start justify-center">
          <span className="text-red-600 font-bold text-xs -mt-1">N</span>
        </div>
        
        <div className="absolute inset-0 flex items-end justify-center">
          <span className="text-gray-600 font-bold text-xs -mb-1">S</span>
        </div>
        
        <div className="absolute inset-0 flex items-center justify-end">
          <span className="text-gray-600 font-bold text-xs -mr-1">E</span>
        </div>
        
        <div className="absolute inset-0 flex items-center justify-start">
          <span className="text-gray-600 font-bold text-xs -ml-1">W</span>
        </div>
      </div>
      
      {/* Center dot (fixed, doesn't rotate) */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-2 h-2 bg-gray-700 rounded-full" />
      </div>
      
      {/* Degree display */}
      <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded font-mono">
        {Math.round(normalizedRotation)}°
      </div>
    </div>
  );
};

export { CompassTracker, CompassDisplay };

